<template>
  <div class="lang-switcher" :class="['theme-' + theme]" v-click-outside="close">
    <!-- 触发器 -->
    <button class="ls-trigger" @click="toggle" :title="$t('header.language')">
      <svg class="ls-globe" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
      <span class="ls-flag">{{ current.flag }}</span>
      <span class="ls-label">{{ current.label }}</span>
      <svg class="ls-caret" :class="{ open }" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5">
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </button>
    <!-- 下拉选项 -->
    <transition name="ls-drop">
      <div v-if="open" class="ls-dropdown">
        <div class="ls-dd-head">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          <span>{{ $t('header.language') }}</span>
        </div>
        <div class="ls-dd-list">
          <a
            v-for="l in $languages"
            :key="l.code"
            href="javascript:void(0);"
            class="ls-option"
            :class="{ active: l.code === $i18n.locale }"
            @click="select(l.code)"
          >
            <span class="ls-opt-flag">{{ l.flag }}</span>
            <span class="ls-opt-label">{{ l.label }}</span>
            <span v-if="l.code === $i18n.locale" class="ls-check">
              <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
            </span>
          </a>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'LanguageSwitcher',
  props: {
    theme: { type: String, default: 'light' }
  },
  data() {
    return { open: false };
  },
  computed: {
    current() {
      const cur = (this.$languages || []).find(l => l.code === this.$i18n.locale);
      return cur || { flag: '🌐', label: 'EN' };
    }
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (e) => {
          if (el !== e.target && !el.contains(e.target) && binding.value) {
            binding.value();
          }
        };
        document.addEventListener('click', el._clickOutside, true);
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside, true);
      }
    }
  },
  methods: {
    toggle() { this.open = !this.open; },
    close() { this.open = false; },
    select(code) { this.$setLocale(code); this.open = false; }
  }
};
</script>

<style scoped>
.lang-switcher { position: relative; display: inline-block; }

/* ===== 触发器 ===== */
.ls-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px 7px 10px;
  border-radius: 22px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s ease;
  white-space: nowrap;
  border: 1px solid transparent;
}
.ls-globe { opacity: 0.55; transition: opacity 0.2s; flex-shrink: 0; }
.ls-trigger:hover .ls-globe { opacity: 1; }

/* light 主题 */
.theme-light .ls-trigger {
  background: linear-gradient(135deg, #f5f7fa, #eef1f6);
  color: #303133;
  border-color: #e4e7ed;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.theme-light .ls-trigger:hover {
  border-color: #ff6a00;
  color: #ff6a00;
  box-shadow: 0 3px 10px rgba(255,106,0,0.15);
  transform: translateY(-1px);
}

/* dark 主题 */
.theme-dark .ls-trigger {
  background: linear-gradient(135deg, #2a3050, #252b48);
  color: #fff;
  border-color: #3a4063;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
}
.theme-dark .ls-trigger:hover {
  border-color: #ff6a00;
  background: linear-gradient(135deg, #ff6a00, #ff8c3a);
  box-shadow: 0 4px 12px rgba(255,106,0,0.3);
  transform: translateY(-1px);
}
.theme-dark .ls-globe { opacity: 0.6; }

.ls-flag { font-size: 15px; line-height: 1; }
.ls-caret { opacity: 0.6; transition: transform 0.2s, opacity 0.2s; flex-shrink: 0; }
.ls-caret.open { transform: rotate(180deg); opacity: 1; }
.ls-trigger:hover .ls-caret { opacity: 1; }

/* ===== 下拉面板 ===== */
.ls-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 220px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06);
  z-index: 200;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.04);
}

/* 顶部标题栏 */
.ls-dd-head {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 12px 16px 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #909399;
  background: linear-gradient(135deg, #fafbfc, #f5f6f8);
  border-bottom: 1px solid #f0f0f0;
}
.ls-dd-head svg { color: #ff6a00; }

/* 列表 */
.ls-dd-list {
  padding: 5px;
  max-height: 340px;
  overflow-y: auto;
}
.ls-dd-list::-webkit-scrollbar { width: 5px; }
.ls-dd-list::-webkit-scrollbar-thumb { background: #ffd4b0; border-radius: 3px; }
.ls-dd-list::-webkit-scrollbar-thumb:hover { background: #ff6a00; }
.ls-dd-list::-webkit-scrollbar-track { background: transparent; }

/* 选项 */
.ls-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 8px;
  color: #4b5563;
  text-decoration: none;
  font-size: 13.5px;
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}
.ls-option:hover {
  background: #fff7f0;
  color: #ff6a00;
  padding-left: 16px;
}
.ls-option.active {
  background: linear-gradient(135deg, #fff7f0, #ffeede);
  color: #ff6a00;
  font-weight: 600;
}
.ls-opt-flag {
  font-size: 20px;
  line-height: 1;
  filter: drop-shadow(0 1px 1px rgba(0,0,0,0.08));
}
.ls-opt-label { flex: 1; }
.ls-check {
  width: 18px; height: 18px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff6a00, #ff9248);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(255,106,0,0.3);
}

/* ===== 过渡动画 ===== */
.ls-drop-enter-active { transition: opacity 0.2s ease, transform 0.25s cubic-bezier(0.16, 1, 0.3, 1); transform-origin: top right; }
.ls-drop-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; transform-origin: top right; }
.ls-drop-enter { opacity: 0; transform: scale(0.9) translateY(-8px); }
.ls-drop-leave-to { opacity: 0; transform: scale(0.95) translateY(-4px); }

/* ===== 移动端 ===== */
@media (max-width: 640px) {
  .ls-trigger { padding: 6px 10px 6px 8px; font-size: 12px; gap: 5px; }
  .ls-globe { display: none; }
  .ls-dropdown { width: 190px; }
  .ls-dd-list { max-height: 280px; }
}
</style>
